import React from 'react';
import { useTranslation } from 'react-i18next';
import { Activity, RefreshCw, Download, AlertTriangle, Pause, Play } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';
import {
  Menubar, MenubarMenu, MenubarTrigger, MenubarContent,
  MenubarItem, MenubarSeparator,
} from '../ui/menubar';

const MonitoringHeader = ({
  realtimeEnabled,
  wsConnected,
  toggleRealtimeMode,
  autoRefresh,
  setAutoRefresh,
  refreshInterval,
  setRefreshInterval,
  downloadAllTerminals,
  clearAllTerminals,
  exportLogs,
  loadPunchLogs,
}) => {
  const { t } = useTranslation();

  return (
  <div className="flex items-center justify-between">
    <div>
      <h1 className="text-3xl font-bold tracking-tight">{t('zktecoComp.monitoringTitle')}</h1>
      <p className="text-muted-foreground">
        {t('zktecoComp.monitoringSubtitle')}
      </p>
    </div>
    <TooltipProvider>
      <div className="flex items-center gap-2 flex-wrap justify-end w-full">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={realtimeEnabled ? 'default' : 'outline'}
              onClick={toggleRealtimeMode}
              className={`shrink-0 w-full sm:w-auto ${wsConnected ? 'bg-green-600 hover:bg-green-700' : realtimeEnabled ? 'bg-yellow-600 hover:bg-yellow-700' : ''}`}
            >
              {wsConnected ? (
                <Activity className="h-4 w-4 mr-2 animate-pulse" />
              ) : realtimeEnabled ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Activity className="h-4 w-4 mr-2" />
              )}
              {wsConnected ? t('zktecoComp.realtimeOn') : realtimeEnabled ? t('zktecoComp.connecting') : t('zktecoComp.realtimeOff')}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('zktecoComp.livePushToggleTooltip')}</TooltipContent>
        </Tooltip>

        <Menubar className="shrink-0 w-full sm:w-auto">
          <MenubarMenu>
            <MenubarTrigger>{t('zktecoComp.actions')}</MenubarTrigger>
            <MenubarContent>
              <MenubarItem onSelect={downloadAllTerminals}>
                <Download className="h-4 w-4 mr-2" /> {t('zktecoComp.downloadPunches')}
              </MenubarItem>
              <MenubarItem onSelect={clearAllTerminals}>
                <AlertTriangle className="h-4 w-4 mr-2" /> {t('zktecoComp.clearAll')}
              </MenubarItem>
              <MenubarSeparator />
              <MenubarItem onSelect={exportLogs}>
                <Download className="h-4 w-4 mr-2" /> {t('zktecoComp.exportCsv')}
              </MenubarItem>
            </MenubarContent>
          </MenubarMenu>
          <MenubarMenu>
            <MenubarTrigger>{t('zktecoComp.options')}</MenubarTrigger>
            <MenubarContent>
              <MenubarItem onSelect={() => setAutoRefresh(!autoRefresh)}>
                {autoRefresh ? <Pause className="h-4 w-4 mr-2" /> : <Play className="h-4 w-4 mr-2" />}
                {autoRefresh ? t('zktecoComp.pauseAutoRefresh') : t('zktecoComp.enableAutoRefresh')}
              </MenubarItem>
              <MenubarSeparator />
              <div className="px-2 py-1.5 text-sm">
                {t('zktecoComp.interval')}
                <Select value={refreshInterval.toString()} onValueChange={(v) => setRefreshInterval(parseInt(v))}>
                  <SelectTrigger className="mt-1 h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5s</SelectItem>
                    <SelectItem value="10">10s</SelectItem>
                    <SelectItem value="30">30s</SelectItem>
                    <SelectItem value="60">1 min</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </MenubarContent>
          </MenubarMenu>
        </Menubar>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button onClick={() => loadPunchLogs()} variant="outline" className="shrink-0 w-full sm:w-auto">
              <RefreshCw className="h-4 w-4 mr-2" /> {t('zktecoComp.refresh')}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('zktecoComp.reloadTooltip')}</TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  </div>
  );
};

export default MonitoringHeader;
